import React, { useState } from 'react';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import QrcodeIcon from 'mdi-material-ui/Qrcode';
import { createPortal } from 'react-dom';
import { Close } from '@material-ui/icons';
import DialogForm from '../DialogForm';
import strings from '../../localization';
import { InvalidPairingBundleError, parsePairingBundle } from '../../services/domiHealth/pairingStorage';
import { PairingBundle } from '../../services/domiHealth/types';

export interface PairingDialogProps {
  open: boolean;
  onClose: () => void;
  onPaired: (bundle: PairingBundle) => void;
}

// Mirrors AddAccountDialog.js's QRScanner integration exactly — same
// plugin, same lifecycle, same reason for the createPortal overlay (the
// native scanner view renders *underneath* the WebView, so the rest of
// the UI has to be hidden rather than layered on top of it).
export default function PairingDialog({ open, onClose, onPaired }: PairingDialogProps) {
  const [pastedCode, setPastedCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scannerStatus, setScannerStatus] = useState<any>();
  const [scannerIsVisible, setScannerIsVisible] = useState(false);

  const presentQrScanner = () => {
    (window as any).QRScanner?.prepare((_: any, status: any) => {
      setScannerStatus(status);
      setScannerIsVisible(true);
    });
  };

  const tryAcceptCode = (raw: string): boolean => {
    try {
      const bundle = parsePairingBundle(raw);
      setError(null);
      onPaired(bundle);
      return true;
    } catch (err) {
      setError(err instanceof InvalidPairingBundleError ? err.message : strings.domiHealth.pairingInvalid);
      return false;
    }
  };

  React.useEffect(() => {
    if (!scannerIsVisible) {
      return;
    }
    const QRScanner = (window as any).QRScanner;
    if (scannerStatus?.denied) {
      QRScanner.openSettings();
      return;
    }
    if (!scannerStatus?.authorized) {
      return;
    }

    const handleScan = (err: any, text: string) => {
      if (err) {
        setScannerIsVisible(false);
        return;
      }
      if (text) {
        setScannerIsVisible(false);
        tryAcceptCode(text);
      }
    };

    const dialogElements = document.querySelectorAll('[role="presentation"]');
    const rootElement = document.getElementById('root');

    QRScanner.scan(handleScan);
    QRScanner.show(() => {
      dialogElements.forEach((el: any) => (el.style.display = 'none'));
      if (rootElement) rootElement.style.display = 'none';
    });

    return () => {
      QRScanner.cancelScan();
      QRScanner.hide(() => {
        dialogElements.forEach((el: any) => (el.style.display = ''));
        if (rootElement) rootElement.style.display = '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerStatus, scannerIsVisible]);

  return (
    <DialogForm
      open={open}
      onEnter={() => {
        setPastedCode('');
        setError(null);
      }}
      onClose={onClose}
      onSubmit={() => tryAcceptCode(pastedCode)}
      fullWidth
    >
      <DialogTitle>{strings.domiHealth.pairDialogTitle}</DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {strings.domiHealth.notPairedDescription}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<QrcodeIcon />}
          fullWidth
          onClick={presentQrScanner}
          style={{ marginBottom: 16 }}
        >
          {strings.domiHealth.scanQrButton}
        </Button>
        <TextField
          label={strings.domiHealth.pastePairingCodeLabel}
          fullWidth
          multiline
          minRows={3}
          variant="outlined"
          margin="normal"
          value={pastedCode}
          onChange={(e) => setPastedCode(e.target.value)}
        />
        {error && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.close}</Button>
        <Button type="submit" color="primary" disabled={!pastedCode}>
          {strings.domiHealth.pairButton}
        </Button>
      </DialogActions>

      {scannerIsVisible &&
        createPortal(
          <div
            style={{
              margin: 8,
              paddingTop: 'env(safe-area-inset-top)',
            }}
          >
            <IconButton color="inherit" onClick={() => setScannerIsVisible(false)}>
              <Close />
            </IconButton>
          </div>,
          document.body,
        )}
    </DialogForm>
  );
}
